import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSeo } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Home as HomeIcon,
  Calendar,
  ChevronRight,
  FileText,
  ArrowLeft,
  Menu,
  X,
  Calculator as CalculatorIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://realestate-stats.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

const News = () => {
  useSeo({
    title: 'Noticias del Mercado Inmobiliario Argentino | Vendetucasa.ar',
    description: 'Análisis y novedades del mercado inmobiliario argentino. Tendencias de precios, regulaciones y consejos para comprar, vender o alquilar en CABA y Zona Norte.',
    canonical: 'https://vendetucasa.ar/noticias',
    ogUrl: 'https://vendetucasa.ar/noticias',
  });
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      if (response.data.success) {
        setArticles(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="relative">
              <svg viewBox="0 0 100 100" className="w-10 h-10">
                <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                <circle cx="25" cy="25" r="5" fill="white"/>
              </svg>
            </div>
            <span className="text-2xl font-bold">
              <span className="text-[#E31C23]">vende</span>
              <span className="text-slate-600">tu</span>
              <span className="text-[#E31C23]">casa</span>
              <span className="text-[#003DA5]">.ar</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-5">
            <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Sobre Mí</Link>
            <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Servicios</Link>
            <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Calculadora</Link>
            <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Barrios</Link>
            <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Propiedades</Link>
            <Link to="/noticias" className="text-[#E31C23] font-medium">Noticias</Link>
            <Link to="/#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">FAQ</Link>
            <Link to="/#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Contacto</Link>
          </nav>
          
          <div className="flex items-center gap-3">
            <Link 
              to="/calculadora" 
              className="calculator-pulse hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-[#003DA5] text-white hover:bg-[#E31C23] hover:scale-110 transition-all duration-200 shadow-md"
              title="Calculadora de Precios"
            >
              <CalculatorIcon className="w-5 h-5" />
            </Link>
            <Button asChild className="hidden sm:flex bg-[#E31C23] hover:bg-[#C11A1F] text-white">
              <Link to="/#appraisal-form">Conocé el Valor de tu Casa</Link>
            </Button>
            
            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon"
              className="md:hidden text-[#003DA5]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-blue-100 py-4">
            <nav className="container mx-auto px-4 flex flex-col space-y-3">
              <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Sobre Mí
              </Link>
              <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Servicios
              </Link>
              <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Calculadora
              </Link>
              <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Barrios
              </Link>
              <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Propiedades
              </Link>
              <Link to="/noticias" className="text-[#E31C23] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Noticias
              </Link>
              <Link to="/#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                FAQ
              </Link>
              <Link to="/#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Contacto
              </Link>
              <Button asChild className="bg-[#E31C23] hover:bg-[#C11A1F] text-white w-full mt-2">
                <Link to="/#appraisal-form" onClick={() => setMobileMenuOpen(false)}>Conocé el Valor de tu Casa</Link>
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Page Title */}
      <section className="bg-gradient-to-br from-[#003DA5] to-[#0051D5] py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Noticias y Artículos
            </h1>
            <p className="text-xl text-blue-100">
              Información actualizada sobre el mercado inmobiliario
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center text-sm text-slate-600">
          <Link to="/" className="hover:text-[#E31C23] flex items-center">
            <HomeIcon className="w-4 h-4 mr-1" />
            Inicio
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-[#003DA5] font-medium">Noticias</span>
        </div>
      </div>

      {/* Articles Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E31C23]"></div>
              <p className="mt-4 text-slate-600">Cargando artículos...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-20 h-20 text-slate-300 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-slate-700 mb-2">Próximamente</h2>
              <p className="text-slate-600 mb-6">Estamos preparando contenido valioso para ti</p>
              <Button asChild variant="outline" className="border-[#003DA5] text-[#003DA5]">
                <Link to="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al Inicio
                </Link>
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {articles.map((article) => (
                <Card key={article.id} className="border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  {article.image_url && (
                    <Link to={`/noticias/${article.slug || article.id}`} className="block h-52 overflow-hidden cursor-pointer">
                      <img 
                        src={article.image_url} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </Link>
                  )}
                  <CardHeader>
                    <div className="flex items-center text-xs text-slate-500 mb-2">
                      <Calendar className="w-3 h-3 mr-1" />
                      {new Date(article.created_at).toLocaleDateString('es-AR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </div>
                    <Link to={`/noticias/${article.slug || article.id}`}>
                      <CardTitle className="text-xl text-[#003DA5] line-clamp-2 group-hover:text-[#E31C23] transition-colors cursor-pointer">
                        {article.title}
                      </CardTitle>
                    </Link>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="text-slate-600 line-clamp-3 mb-4 prose prose-sm"
                      dangerouslySetInnerHTML={{ 
                        __html: article.excerpt || article.content.substring(0, 150) + '...' 
                      }}
                    />
                    <Button 
                      asChild
                      variant="link" 
                      className="text-[#E31C23] hover:text-[#C11A1F] p-0"
                    >
                      <Link to={`/noticias/${article.slug || article.id}`}>
                        Leer más <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003DA5] text-white py-12 mt-auto">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <svg viewBox="0 0 100 100" className="w-16 h-16">
                    <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                    <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                    <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                    <circle cx="25" cy="25" r="5" fill="white"/>
                  </svg>
                </div>
                <span className="text-4xl font-bold">
                  <span className="text-white">vende</span>
                  <span className="text-blue-200">tu</span>
                  <span className="text-white">casa</span>
                  <span className="text-blue-300">.ar</span>
                </span>
              </div>
            </div>
            <p className="text-blue-100 mb-2">Vendetucasa.ar - Portal Inmobiliario</p>
            <p className="text-blue-200 text-sm">
              © {new Date().getFullYear()} Vendetucasa.ar - Todos los derechos reservados
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default News;
